import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, FinancierViewModel, FinancierService } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-financier-details',
    templateUrl: './financier-details.component.html',
    styleUrls: ['./financier-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FinancierDetailsComponent implements OnInit, CanComponentDeactivate {

    financier: FinancierViewModel;
    users$: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    financierForm: UntypedFormGroup;

    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private financierService: FinancierService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();

        if (this.id) {
            this.financierService.getFinancierById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.isNew = true;
            this.financier = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    dataLoaded(financier: FinancierViewModel) {
        this.financier = financier;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.financierForm.controls[name];
    }

    private buildForm() {
        this.financierForm = this.formBuilder.group({
            financierId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.financier) {
            this.isNew = true;
            this.financier = this.initializeEmptyObject();
        }

        this.financierForm.reset({
            financierId: this.financier.financierId || '',
            name: this.financier.name || '',
            createdBy: this.financier.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.financier.createdDate, true) || '',
            updatedBy: this.financier.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.financier.updatedDate, true) || '',
        });

        if (this.financier.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.financier.createdBy).pipe(map(t => t.item2));
        }
        if (this.financier.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.financier.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): FinancierViewModel {
        var result = <FinancierViewModel>{};
        return result;
    }

    private prepareSave(): FinancierViewModel {
        const formModel = this.financierForm.value;

        return {
            financierId: this.isNew ? null : formModel.financierId,
            name: formModel.name
        };
    }

    public save() {
        if (!this.financierForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const financier = this.prepareSave();

        if (this.isNew) {
            this.financierService.createFinancier(financier).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.financierService.updateFinancier(financier.financierId, financier).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.financierForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }


    private saveCompleted(financier: FinancierViewModel) {
        this.financier = financier;
        this.id = financier.financierId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), financier.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReferenceData);
    }

}
